import React, { useState, useEffect, useContext } from 'react';
import { Grid, Typography, Box, Pagination } from '@mui/material';
import { AuthContext } from '../../../Helpers/Contexts/authContext';
import { AuthUser } from '../../../interfaces/Contexts';
import JobCard from '../../../Components/JobCards';
import { AppButton } from '../../../Components/AppButton';
import { getJobs, getJobsApply, getJobsBookmark } from '../../../Utils/apiService';
import routes from '../../../Utils/routes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

interface CareerDetailsContentProps {
  initialTab: string;
  onBack: () => void;
}

const CareerDetailsContent: React.FC<CareerDetailsContentProps> = ({ initialTab, onBack }) => {
  const navigate = useNavigate();
  const {
    state: { user },
  } = useContext(AuthContext);
  const userData = user as AuthUser;

  // Tab state
  const [activeTab, setActiveTab] = useState(initialTab);

  // Data states
  const [appliedData, setAppliedData] = useState<any[]>([]);
  const [savedJobsData, setSavedJobsData] = useState<any[]>([]);
  const [jobsData, setJobsData] = useState<any[]>([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [displayedData, setDisplayedData] = useState<any[]>([]);
  const itemsPerPage = 10;

  // Pagination helper function
  const updateDisplayedData = (data: any[], page: number = 1) => {
    // Fix null data issue
    if (!data || !Array.isArray(data)) {
      setDisplayedData([]);
      setCurrentPage(1);
      return;
    }
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setDisplayedData(data.slice(startIndex, endIndex));
    setCurrentPage(page);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    const currentData = getCurrentTabData();
    updateDisplayedData(currentData, value);
  };

  const getCurrentTabData = () => {
    switch (activeTab) {
      case 'Applied':
        return appliedData || [];
      case 'Saved':
        return savedJobsData || [];
      case 'Posted by me':
        return jobsData || [];
      default:
        return [];
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const data = tab === 'Applied' ? appliedData :
      tab === 'Saved' ? savedJobsData : jobsData;
    updateDisplayedData(data || [], 1);
  };

  // Data fetching functions
  const getJobAppliedDetails = () => {
    getJobsApply(userData.applicantId)
      .then((res) => {
        const appliedJobs = res.data.appliedJobs || [];
        setAppliedData(appliedJobs);
        if (activeTab === 'Applied') {
          updateDisplayedData(appliedJobs, 1);
        }
      })
      .catch((err) => {
        console.error(err);
        setAppliedData([]);
      });
  };

  const getSavedJobsDetails = () => {
    getJobsBookmark(userData.applicantId)
      .then((res) => {
        const savedJobs = res.data.savedJobs || [];
        setSavedJobsData(savedJobs);
        if (activeTab === 'Saved') {
          updateDisplayedData(savedJobs, 1);
        }
      })
      .catch((err) => {
        console.error(err);
        setSavedJobsData([]);
      });
  };

  const getJobDetails = () => {
    getJobs(userData.applicantId)
      .then((res) => {
        const myJobs = res.data.myJobs || [];
        setJobsData(myJobs);
        if (activeTab === 'Posted by me') {
          updateDisplayedData(myJobs, 1);
        }
      })
      .catch((err) => {
        console.error(err);
        setJobsData([]);
      });
  };

  useEffect(() => {
    getJobAppliedDetails();
    getSavedJobsDetails();
    getJobDetails();
  }, []);

  useEffect(() => {
    const currentData = getCurrentTabData();
    updateDisplayedData(currentData || [], 1);
  }, [activeTab]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const renderEmptyState = (tabName: string) => (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '300px',
      textAlign: 'center',
      color: '#666'
    }}>
      <Typography variant="h6" sx={{ mb: 1, color: '#999' }}>
        No {tabName} Jobs
      </Typography>
      <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
        {tabName === 'Applied' && "You haven't applied to any jobs yet."}
        {tabName === 'Saved' && "You haven't saved any jobs yet."}
        {tabName === 'Posted by me' && "You haven't posted any jobs yet."}
      </Typography>
      {tabName === 'Posted by me' && (
        <AppButton
          size="small"
          variant="contained"
          onClick={() => navigate(routes.postJob)}
          others={{
            backgroundColor: '#FF9500',
            color: 'white',
            borderRadius: '6px',
            height: '36px',
            fontSize: '12px',
            fontWeight: 500
          }}
        >
          + Post your first job
        </AppButton>
      )}
    </Box>
  );

  const currentData = getCurrentTabData();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>


      {/* Tab Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(270deg, rgba(191, 206, 255, 0) 0%, #BFCEFF 100%)',
          zIndex: 1
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, position: 'relative' }}>
          <ArrowBackIcon
            sx={{
              cursor: 'pointer',
              mr: 2,
              color: '#666',
              '&:hover': { color: '#000' }
            }}
            onClick={onBack}
          />
          {['Applied', 'Saved', 'Posted by me'].map((tab) => (
            <Box
              key={tab}
              onClick={() => handleTabChange(tab)}
              sx={{
                cursor: 'pointer',
                padding: '8px 16px',
                position: 'relative',
                color: activeTab === tab ? '#000' : '#666',
                fontWeight: activeTab === tab ? 600 : 400,
                fontSize: '14px',
                transition: 'all 0.2s ease',
                '&:hover': { color: '#000' },
                '&::after': activeTab === tab ? {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  backgroundColor: '#FF9500',
                  zIndex: 2
                } : {}
              }}
            >
              {tab}
            </Box>
          ))}
        </Box>

        {/* Action buttons for different tabs */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* View More button for Applied and Posted by me tabs */}
          {(activeTab === 'Applied' || activeTab === 'Posted by me') && currentData.length > 0 && (
            <AppButton
              size="small"
              variant="outlined"
              onClick={onBack}
              others={{
                color: '#B8860B',
                borderColor: '#B8860B',
                borderRadius: '6px',
                height: '32px',
                fontSize: '12px',
                fontWeight: 500
              }}
            >
              View more
            </AppButton>
          )}

          {/* Post Job button for Posted by me tab */}
          {activeTab === 'Posted by me' && (
            <AppButton
              size="small"
              variant="contained"
              onClick={() => navigate(routes.postJob)}
              others={{
                backgroundColor: '#FF9500',
                color: '#5B4200',
                borderRadius: '6px',
                height: '32px',
                fontSize: '12px',
                fontWeight: 500
              }}
            >
              + Post a job
            </AppButton>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: '400px' }}>
        {displayedData.length > 0 ? (
          <Grid container spacing={2}>
            {displayedData.map((val: any, index: number) => (
              <Grid key={index} item xs={12} sm={12} md={12} xl={4} lg={4}>
                <JobCard value={val} />
              </Grid>
            ))}
          </Grid>
        ) : (
          renderEmptyState(activeTab)
        )}
      </Box>

      {/* Pagination */}
      {currentData.length > itemsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(currentData.length / itemsPerPage)}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="medium"
            sx={{
              '& .MuiPaginationItem-root': {
                '&.Mui-selected': {
                  backgroundColor: '#FF9500',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#E8860B',
                  },
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default CareerDetailsContent;
